import {
  CompanyProductSubscription,
  CompanyProductSubscriptionStatus,
  NextPaymentStatus,
} from '../../../../common/interfaces/company-product-subscription';
import { EmployerPurchaseProductType } from '../../../../common/interfaces/entities';
import { PaymentOrderSource } from '../../../../common/interfaces/payment-order-source';
import { ROUTES } from '../../../../common/routes';
import {
  OrderListStatus,
  PaymentOrderProvider,
  PaymentOrderStatus,
  PaymentPayStatus,
  RechargeOrderStatusResp,
  VirtualProductOrderStatusResp,
} from '../../../PaymentOrder/types';
import {
  CompanyCreditInfo,
  CreditProductPurchaseOrder,
  CreditProductPurchaseOrderStatus,
  CreditPurchaseOrderStatus,
  PaymentService,
  PurchaseOrderPurchaseType,
} from '../../types';
import { CreditPurchaseOrderForAfterPaymentWithPaymentDetails } from '../types';

const statusMap: Record<PaymentOrderStatus, CreditPurchaseOrderStatus> = {
  PENDING_PAYMENT: CreditPurchaseOrderStatus.PENDING,
  PARTIALLY_PAID: CreditPurchaseOrderStatus.PENDING,
  PROCESSING: CreditPurchaseOrderStatus.PENDING,
  COMPLETED: CreditPurchaseOrderStatus.COMPLETE,
  FAILED: CreditPurchaseOrderStatus.EXPIRED,
  CLOSED: CreditPurchaseOrderStatus.CANCELLED,
  REFUNDED: CreditPurchaseOrderStatus.CANCELLED,
  EXPIRED: CreditPurchaseOrderStatus.EXPIRED,
};

const paymentPayStatusToCreditProductPurchaseOrderStatus: Record<
  PaymentPayStatus,
  CreditProductPurchaseOrderStatus
> = {
  [PaymentPayStatus.PENDING]: CreditProductPurchaseOrderStatus.PENDING,
  [PaymentPayStatus.SUCCESS]: CreditProductPurchaseOrderStatus.SUCCESS,
  [PaymentPayStatus.FAILED]: CreditProductPurchaseOrderStatus.FAILED,
};

const providerToPaymentService: Partial<Record<string, PaymentService>> = {
  [PaymentOrderProvider.XENDIT]: PaymentService.XENDIT_INVOICE,
  [PaymentOrderProvider.TWO_C_TWO_P]: PaymentService.PAYMENT_TOKEN_2C2P,
  [PaymentOrderProvider.REVENUE_CAT]: PaymentService.XENDIT_QR_CODE,
};

const defaultCompanyCreditInfo: CompanyCreditInfo = {
  totalAmount: 0,
  nextExpiringCredit: null,
};

export const mapRechargeOrderStatusResp = (
  resp: RechargeOrderStatusResp
): CreditPurchaseOrderForAfterPaymentWithPaymentDetails =>
  ({
    // CreditPurchaseOrder base fields
    id: resp.orderNumber,
    status: statusMap[resp.orderStatus] ?? CreditPurchaseOrderStatus.PENDING,
    redirectPathAfterPayment:
      resp.bizContext?.sourcePage || `/${ROUTES.features}`,
    totalCredits: resp.totalCredits,
    createdAt: resp.createdAt,
    totalPayment: resp.paidAmount,
    currency: resp.currency,
    paymentExpiryAt: resp.expiryTime ?? '',
    purchaseType: PurchaseOrderPurchaseType.ONE_TIME,
    activeSubscriptionInformation: null as CompanyProductSubscription | null,
    paymentRequestInfo: null,
    // Union arm: derived from paymentDetails.providerCode, falls back to
    // XENDIT_QR_CODE when no payment has been initiated yet.
    paymentService:
      (resp.paymentDetails?.providerCode &&
        providerToPaymentService[resp.paymentDetails.providerCode]) ||
      PaymentService.XENDIT_QR_CODE,
    qrCodeString: '',
    paymentUrl: null,
    // CreditPurchaseOrderForAfterPayment extension
    company: { companyCreditInfo: defaultCompanyCreditInfo },
    creditProductPurchaseOrder: null,
    paymentDetails: resp.paymentDetails,
    canRetryPayment: resp.displayStatus === OrderListStatus.PENDING,
    hasPendingOrder: false,
  }) as unknown as CreditPurchaseOrderForAfterPaymentWithPaymentDetails;

const mapProductType = (productType: string): EmployerPurchaseProductType => {
  const knownTypes = new Set<string>(
    Object.values(EmployerPurchaseProductType)
  );
  return knownTypes.has(productType)
    ? (productType as EmployerPurchaseProductType)
    : EmployerPurchaseProductType.HOT_JOB;
};

const buildCreditProductPurchaseOrder = (
  resp: VirtualProductOrderStatusResp,
  productStatus: CreditProductPurchaseOrderStatus,
  productType: EmployerPurchaseProductType
): CreditProductPurchaseOrder => {
  const trialInformationDetail = resp.companyProductInventory
    ?.trialInformationDetail ?? {
    startDate: null,
    endDate: null,
    isActive: false,
    isExtended: false,
    timePeriod: null,
    timePeriodUnit: null,
  };

  const buildProductVariantDetail = () => {
    if (
      productType === EmployerPurchaseProductType.PREMIUM_PLAN &&
      resp.productDetails?.planType
    ) {
      return { planType: resp.productDetails.planType };
    }

    if (
      productType === EmployerPurchaseProductType.HOT_JOB &&
      resp.productDetails?.boostPeriod
    ) {
      return {
        durationCount: resp.productDetails.boostPeriod,
        durationUnit: resp.productDetails.boostPeriodUnit ?? 'days',
      };
    }

    return null;
  };

  const productVariantDetail = buildProductVariantDetail();

  return {
    id: resp.orderNumber,
    status: resp.paymentDetails?.paymentStatus
      ? paymentPayStatusToCreditProductPurchaseOrderStatus[
          resp.paymentDetails.paymentStatus
        ]
      : productStatus,
    productQuantity: 0,
    productTimePeriod: null,
    productTimePeriodUnit: null,
    creditPurchaseOrder: null,
    companyProductInventory: resp.companyProductInventory
      ? {
          amount: resp.companyProductInventory.amount,
          expiryDateTime: resp.companyProductInventory.expiryDateTime,
          trialInformationDetail,
        }
      : null,
    product: {
      type: productType,
      variantDetail: productVariantDetail,
    },
    afterPurchaseActionLog: resp.bizContext?.jobId
      ? {
          relatedJobId: resp.bizContext.jobId,
          job: null,
        }
      : null,
    purchaseProductSpecificDetails: resp.metadata?.acquisitionType
      ? { purchaseType: resp.metadata.acquisitionType }
      : null,
    creditBenefit: resp.promotionSnapshot?.creditBenefit ?? null,
    purchaseType: resp.purchaseType,
    activeSubscriptionInformation: resp.subscriptionInfo?.nextRenewalTime
      ? {
          id: '',
          nextPaymentChargeDate: resp.subscriptionInfo.nextRenewalTime,
          nextPaymentStatus: NextPaymentStatus.SCHEDULED,
          subscriptionStatus: CompanyProductSubscriptionStatus.ACTIVE,
          paymentService: PaymentService.XENDIT_SUBSCRIPTION,
          orderSource: PaymentOrderSource.WEB,
          creditPurchaseOrderSubscriptionDetail: null,
        }
      : null,
    failureType: resp.failureType ?? null,
  } as CreditProductPurchaseOrder;
};

export const mapVirtualProductOrderStatusResp = (
  resp: VirtualProductOrderStatusResp
): CreditPurchaseOrderForAfterPaymentWithPaymentDetails => {
  const productType = mapProductType(resp.productDetails?.productType ?? '');

  // Only populate creditProductPurchaseOrder for terminal statuses
  // (COMPLETED → SUCCESS, FAILED/CLOSED → FAILED). Non-terminal statuses
  // (PENDING_PAYMENT, PARTIALLY_PAID, PROCESSING) leave it null so the
  // dispatcher hits CREDIT_PURCHASE_ORDER_PENDING (renders spinner).
  let creditProductPurchaseOrder: CreditProductPurchaseOrder | null = null;

  if (resp.orderStatus === PaymentOrderStatus.COMPLETED) {
    creditProductPurchaseOrder = buildCreditProductPurchaseOrder(
      resp,
      CreditProductPurchaseOrderStatus.SUCCESS,
      productType
    );
  } else if (
    resp.orderStatus === PaymentOrderStatus.FAILED ||
    resp.orderStatus === PaymentOrderStatus.CLOSED ||
    resp.orderStatus === PaymentOrderStatus.REFUNDED
  ) {
    creditProductPurchaseOrder = buildCreditProductPurchaseOrder(
      resp,
      CreditProductPurchaseOrderStatus.FAILED,
      productType
    );
  }

  return {
    id: resp.orderNumber,
    status: statusMap[resp.orderStatus] ?? CreditPurchaseOrderStatus.PENDING,
    redirectPathAfterPayment:
      resp.bizContext?.sourcePage || `/${ROUTES.features}`,
    totalCredits: resp.paidCredits,
    createdAt: resp.createdAt,
    totalPayment: resp.paidAmount,
    currency: resp.currency,
    paymentExpiryAt: resp.expiryTime ?? '',
    purchaseType: resp.purchaseType,
    activeSubscriptionInformation: null as CompanyProductSubscription | null,
    paymentRequestInfo: null,
    paymentService:
      (resp.paymentDetails?.providerCode &&
        providerToPaymentService[resp.paymentDetails.providerCode]) ||
      PaymentService.XENDIT_QR_CODE,
    qrCodeString: '',
    paymentUrl: null,
    company: { companyCreditInfo: defaultCompanyCreditInfo },
    creditProductPurchaseOrder,
    paymentDetails: resp.paymentDetails,
    canRetryPayment: resp.displayStatus === OrderListStatus.PENDING,
    // REST BE doesn't have the "pending order blocking this product order"
    // concept — always false. PROCESSING/PARTIALLY_PAID map to status=PENDING
    // which dispatches to CREDIT_PURCHASE_ORDER_PENDING (spinner).
    hasPendingOrder: false,
  } as unknown as CreditPurchaseOrderForAfterPaymentWithPaymentDetails;
};
